import orderBy from "lodash/orderBy";
import some from "lodash/some";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { Alert } from "PFComponents/alert";
import { TooltipIcon } from "PFComponents/tooltip";
import { Typography } from "PFComponents/typography";
import { useActivityEconomicsScenarioReset } from "PFCore/hooks/queries/activity/use_activity_economics_scenario_reset";
import { ClosureReasonKey, EconomicsVacancy, Id } from "PFTypes";
import { useTranslation } from "react-i18next";

import { ClosedVacanciesTable } from "../vacancies_table";
import css from "./reset_vacancies_row.module.scss";

type ResetVacanciesRowProps = {
  vacancies: EconomicsVacancy[];
  scenarioId: Id;
  isEditable: boolean;
};

export const ResetVacanciesRow = ({ vacancies, scenarioId, isEditable }: ResetVacanciesRowProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "scenario" });
  const { showGrowl } = useGrowlStoreActions();

  const { mutateAsync: resetScenario } = useActivityEconomicsScenarioReset(scenarioId);

  const closedVacancies = orderBy(
    vacancies.filter(({ opened }) => !opened),
    [
      (vacancy) => !!vacancy.profile,
      (vacancy) => vacancy.closureReason === ClosureReasonKey.FilledExternally
    ],
    ["desc", "desc"]
  );
  const filledVacancies = vacancies.filter(({ profile }) => profile);
  const assigneesIds = vacancies.map(({ assignment }) => assignment?.profile?.id).filter(Boolean);
  const isResetButtonVisible = some(filledVacancies, ({ profile }) => !assigneesIds.includes(profile!.id));

  const onResetVacancies = () => {
    const { id: activityId, name: roleName } = vacancies[0].activity;

    resetScenario({ scenarioId, activityId }).then(() => {
      showGrowl({
        message: t("roleVacanciesHaveBeenReset", { roleName })
      });
    });
  };

  return (
    <Alert
      kind="general"
      hideIcon
      title={
        <>
          <TooltipIcon
            content={
              <div className={css.tooltipTableWrapper}>
                <ClosedVacanciesTable vacancies={closedVacancies} />
              </div>
            }
            tooltipProps={{
              interactive: true,
              tooltipContainerProps: { style: { maxWidth: 500 } }
            }}
          />
          <Typography variant="bodyRegular">
            {t("vacanciesClosed", { closed: closedVacancies.length, all: vacancies.length })}
          </Typography>
        </>
      }
      action={
        isResetButtonVisible
          ? { text: t("resetVacancies"), kind: "text", onClick: onResetVacancies, disabled: !isEditable }
          : undefined
      }
      className={css.alert}
    />
  );
};
